import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Arrow, Gauge, PROJECT_COLORS, ProjectDiagram } from "@/components/site/parts";
import NotFound from "@/pages/NotFound";
import { useCopy } from "@/lib/copy";

const GAUGE_COLORS = ["var(--blue)", "#FFC83D", "#3DDC84", "#FF3D3D"];

// Split the case study into sections, one per <h2>.
function toSteps(html, fallback) {
  if (!html?.trim()) {
    return fallback ? [{ title: "Context", html: fallback.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p)}</p>`).join("") }] : [];
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const steps = [];
  let cur = null;
  Array.from(doc.body.firstChild.childNodes).forEach((node) => {
    if (node.nodeName === "H2") {
      cur = { title: node.textContent.trim(), html: "" };
      steps.push(cur);
      return;
    }
    if (!cur) {
      cur = { title: "Context", html: "" };
      steps.push(cur);
    }
    cur.html += node.outerHTML ?? node.textContent;
  });
  return steps;
}

function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

export default function ProjectDetail() {
  const { id } = useParams();
  const t = useCopy();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  }, []);

  const index = projects ? projects.findIndex((p) => p.id === id) : -1;
  const p = index >= 0 ? projects[index] : null;
  const steps = useMemo(() => (p ? toSteps(p.case_study_html, p.description) : []), [p]);

  if (!projects) {
    return <section className="bx" style={{ paddingTop: 32 }}><p className="kicker">Loading project…</p></section>;
  }
  if (!p) return <NotFound />;

  const next = projects.length > 1 ? projects[(index + 1) % projects.length] : null;
  const stack = p.tech_stack || [];
  const [m1, m2] = p.metrics || [];
  const { bg, ink } = PROJECT_COLORS[index % PROJECT_COLORS.length];
  const dark = bg === "#1C1F26";
  const spec = [
    ["ROLE", p.role],
    ["TEAM", p.team_size],
    ["TIMEFRAME", p.period || p.year],
    ["TECH", stack[0]],
    ["STACK", stack.slice(1, 4).join(" · ")],
    ["RESULT", m1 && `${m1.value} ${m1.label.toLowerCase()}`, true],
    ["IMPACT", m2 && `${m2.value} ${m2.label.toLowerCase()}`, true],
  ].filter(([, v]) => v);

  return (
    <>
      <section className="bx" style={{ paddingTop: 16 }}>
        <div className="rise d1 padl split project-hero" style={{ background: bg, color: ink }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
            <Link to="/projects" className="mono" style={{ fontSize: 12, textTransform: "uppercase" }}>{t("project.back")}</Link>
            <span className="mono" style={{ fontSize: 12, textTransform: "uppercase" }}>
              {[`PROJECT ${String(index + 1).padStart(2, "0")}`, p.company, p.category].filter(Boolean).join(" · ")}
            </span>
            <h1 className="hero-h stretch" style={{ fontSize: 64, animationDelay: ".2s", overflowWrap: "anywhere" }}>{p.title}</h1>
            <p style={{ margin: 0, fontSize: 18, lineHeight: 1.6 }}>{p.summary}</p>
            {(p.live_url || p.repo_url) && (
              <div className="project-links">
                {p.live_url && <a href={p.live_url} target="_blank" rel="noopener noreferrer" style={{ borderColor: ink }}>Live site ↗</a>}
                {p.repo_url && <a href={p.repo_url} target="_blank" rel="noopener noreferrer" style={{ borderColor: ink }}>Source ↗</a>}
              </div>
            )}
          </div>
          <div className="bob project-details-card">
            {p.cover_image ? (
              <img src={p.cover_image} alt="" style={{ width: "100%", borderRadius: 12, border: "2px solid #15171C", aspectRatio: "16 / 10", objectFit: "cover" }} />
            ) : (
              dark ? null : <div className="hide-m" style={{ display: "flex", justifyContent: "center" }}><ProjectDiagram ink="#15171C" /></div>
            )}
            <span className="disp" style={{ fontSize: 16, fontWeight: 900, fontVariationSettings: "'wdth' 125" }}>PROJECT DETAILS</span>
            <dl className="mono project-details-list">
              {spec.map(([k, v, hi]) => (
                <div key={k} style={{ display: "contents" }}>
                  <dt>{k}</dt>
                  <dd style={hi ? { color: "var(--blue)", fontWeight: 600 } : undefined}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {p.metrics?.length > 0 && (
        <section className="bx" style={{ paddingTop: 16 }}>
          <div className="g4">
            {p.metrics.slice(0, 4).map((m, i) => (
              <Gauge key={i} pct={m.pct} value={m.value} label={m.label.toUpperCase()} color={GAUGE_COLORS[i]} delay={`${0.2 + i * 0.1}s`} />
            ))}
          </div>
        </section>
      )}

      {steps.length > 0 && (
        <section className="bx" style={{ paddingTop: 56 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 40, alignItems: "flex-start" }}>
            {steps.length > 1 && (
              <nav aria-label="Sections" className="hide-m mono step-nav">
                <span style={{ color: "#6B6A63" }}>CONTENTS</span>
                {steps.map((s, i) => (
                  <a key={i} href={`#s${i + 1}`}>{String(i + 1).padStart(2, "0")} {s.title}</a>
                ))}
              </nav>
            )}
            <div style={{ flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 40, maxWidth: 780 }}>
              {steps.map((s, i) => (
                <div key={i} className="step rv" id={`s${i + 1}`}>
                  <span className="step-n" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <div className="step-body">
                    <span className="kicker">SECTION {String(i + 1).padStart(2, "0")}</span>
                    <h2 className="step-h">{s.title}</h2>
                    <div className="article" dangerouslySetInnerHTML={{ __html: s.html }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {next && (
        <section className="bx" style={{ paddingTop: 40 }}>
          <Link to={`/projects/${next.id}`} className="project-card rv padl next-project">
            <span style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
              <span className="mono" style={{ fontSize: 12, color: "#A3A8B4" }}>NEXT PROJECT · {String(projects.indexOf(next) + 1).padStart(2, "0")}</span>
              <span className="disp" style={{ fontSize: 36, fontWeight: 900, fontVariationSettings: "'wdth' 118", textTransform: "uppercase", overflowWrap: "anywhere" }}>{next.title}</span>
            </span>
            <span aria-hidden="true" className="arrow-box" style={{ width: 56, height: 56, borderRadius: 14, background: "#FFC83D", color: "#15171C" }}><Arrow /></span>
          </Link>
        </section>
      )}
    </>
  );
}
